import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { 
  Users, 
  Home, 
  Handshake, 
  Bell, 
  MessageCircle, 
  User, 
  Calendar,
  Shield,
  Menu,
  X,
  Mail,
  FileText,
  Building2,
  Palmtree,
  BarChart3,
  Image,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import { useState } from 'react';
import liveStayLogo from '@/assets/live-stay-logo.png';

interface AdminSidebarProps {
  isOpen: boolean;
  isCollapsed: boolean;
  onToggle: () => void;
  onCollapseToggle: () => void;
}

const AdminSidebar = ({ isOpen, isCollapsed, onToggle, onCollapseToggle }: AdminSidebarProps) => {
  const navigate = useNavigate();
  const location = useLocation();

  const menuItems = [
    {
      id: 'dashboard',
      title: 'Dashboard',
      icon: BarChart3,
      href: '/admin/dashboard',
      description: 'Visão geral do sistema'
    },
    {
      id: 'users',
      title: 'Usuários',
      icon: Users,
      href: '/admin/usuarios',
      description: 'Gerenciar usuários'
    },
    {
      id: 'properties',
      title: 'Imóveis',
      icon: Home,
      href: '/admin/imoveis',
      description: 'Gerenciar imóveis'
    },
    {
      id: 'partners',
      title: 'Parceiros',
      icon: Handshake,
      href: '/admin/parceiros',
      description: 'Gerenciar parceiros'
    },
    {
      id: 'resorts',
      title: 'Resorts',
      icon: Palmtree,
      href: '/admin/resorts',
      description: 'Dashboard dos empreendimentos'
    },
    {
      id: 'developments',
      title: 'Empreendimentos',
      icon: Building2,
      href: '/admin/empreendimentos',
      description: 'Gerenciar empreendimentos'
    },
    {
      id: 'banners',
      title: 'Banners',
      icon: Image,
      href: '/admin/banners',
      description: 'Gerenciar banners'
    },
    {
      id: 'contacts',
      title: 'Contatos',
      icon: Mail,
      href: '/admin/contatos',
      description: 'Mensagens de contato'
    },
    {
      id: 'bookings',
      title: 'Reservas',
      icon: Calendar,
      href: '/admin/reservas',
      description: 'Gerenciar reservas'
    }
  ];

  const handleNavigation = (href: string) => {
    navigate(href);
    if (window.innerWidth < 768) {
      onToggle(); // Fechar sidebar em mobile após navegação
    }
  };

  return (
    <>
      {/* Overlay para mobile */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 md:hidden"
          onClick={onToggle}
        />
      )}
      
      {/* Sidebar */}
      <div className={cn(
        "fixed left-0 top-0 h-screen bg-white dark:bg-gray-900 border-r border-gray-200 dark:border-gray-700 z-50 transform transition-all duration-300 ease-in-out flex-shrink-0",
        isOpen ? "translate-x-0" : "-translate-x-full",
        isCollapsed ? "w-20" : "w-64",
        "md:translate-x-0 md:static md:z-auto md:h-full"
      )}>
        <div className={cn(
          "h-full flex flex-col",
          isCollapsed ? "p-2" : "p-4"
        )}>
          {/* Logo e Toggle */}
          <div className={cn(
            "flex items-center mb-6",
            isCollapsed ? "justify-center" : "justify-between"
          )}>
            {!isCollapsed && (
              <div className="flex items-center gap-3">
                <img 
                  src={liveStayLogo} 
                  alt="Live Stay" 
                  className="h-16 w-auto"
                />
              </div>
            )}
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={onToggle}
                className="md:hidden"
              >
                <X className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={onCollapseToggle}
                className="hidden md:flex"
                title={isCollapsed ? "Expandir menu" : "Recolher menu"}
              >
                {isCollapsed ? (
                  <ChevronRight className="h-5 w-5" />
                ) : (
                  <ChevronLeft className="h-5 w-5" />
                )}
              </Button>
            </div>
          </div>
          
          <nav className={cn(
            "space-y-2 flex-1",
            isCollapsed ? "flex flex-col items-center" : ""
          )}>
            {menuItems.map((item) => {
              const IconComponent = item.icon;
              const isActive = location.pathname === item.href;
              
              return (
                <Button
                  key={item.id}
                  variant={isActive ? "default" : "ghost"}
                  className={cn(
                    "h-auto transition-all",
                    isCollapsed 
                      ? "w-12 h-12 p-0 justify-center rounded-lg" 
                      : "w-full justify-start p-3 text-left",
                    isActive 
                      ? "bg-primary text-primary-foreground" 
                      : "hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-foreground"
                  )}
                  onClick={() => handleNavigation(item.href)}
                  title={isCollapsed ? item.title : undefined}
                >
                  {isCollapsed ? (
                    <IconComponent className="h-7 w-7 flex-shrink-0" />
                  ) : (
                    <div className="flex items-center space-x-3 w-full">
                      <IconComponent className="h-6 w-6 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium truncate">
                          {item.title}
                        </div>
                        <div className="text-xs opacity-70 truncate">
                          {item.description}
                        </div>
                      </div>
                    </div>
                  )}
                </Button>
              );
            })}
          </nav>
        </div>
      </div>
    </>
  );
};

export default AdminSidebar;
